// Ventas, abonos y cartera (fase C). La base calcula y valida todo (supabase/migrations/20261003c_sales.sql); aquí solo se
// muestra, se filtra y se exporta lo que ella guardó. Diseño: docs/estrategia/MODULO_FACTURACION.md
import { localDateStr } from './customers';
import { money } from './quotes';

export const SALE_STATUS_LABELS = { por_cobrar: 'Por cobrar', parcial: 'Abonada', pagada: 'Pagada', anulada: 'Anulada' };

export const SALE_STATUS_TONES = {
  por_cobrar: 'bg-amber-500/20 text-amber-200 border-amber-500/40',
  parcial: 'bg-sky-500/20 text-sky-200 border-sky-500/40',
  pagada: 'bg-emerald-500/20 text-emerald-200 border-emerald-500/40',
  anulada: 'bg-slate-700/40 text-slate-400 border-slate-600/40'
};

export const PAYMENT_METHODS = [['efectivo', 'Efectivo'], ['transferencia', 'Transferencia'], ['nequi', 'Nequi'], ['daviplata', 'Daviplata'], ['tarjeta', 'Tarjeta'], ['otro', 'Otro']];
export const PAYMENT_METHOD_LABELS = Object.fromEntries(PAYMENT_METHODS);

// Dinero en centavos enteros: sumar y restar sin errores de coma flotante.
export const toCents = (n) => Math.round(Number(n || 0) * 100);
export const fromCents = (c) => c / 100;

export const balanceOf = (sale) => fromCents(toCents(sale?.total) - toCents(sale?.paid_total));

export const isOpenSale = (sale) => sale?.status === 'por_cobrar' || sale?.status === 'parcial';

// Fecha desde la que se cuenta el atraso: el vencimiento y, si no lo hay, el día de la venta (en hora local).
export const referenceDate = (sale) => {
  if (sale?.due_date) return String(sale.due_date).slice(0, 10);
  return sale?.created_at ? localDateStr(new Date(sale.created_at)) : null;
};

const dayNumber = (ymd) => { const [y, m, d] = String(ymd).split('-').map(Number); return Math.floor(Date.UTC(y, m - 1, d) / 86400000); };

// Días de atraso (0 si no ha vencido). Una venta sin saldo o anulada no tiene atraso.
export const daysOverdue = (sale, today = localDateStr()) => {
  if (!isOpenSale(sale)) return 0;
  const ref = referenceDate(sale);
  if (!ref) return 0;
  return Math.max(0, dayNumber(today) - dayNumber(ref));
};

// "Vencida" solo si tiene fecha de vencimiento propia y ya pasó (sin fecha, la venta se cobra al contado: no se marca vencida).
export const isOverdue = (sale, today = localDateStr()) => isOpenSale(sale) && Boolean(sale.due_date) && daysOverdue(sale, today) > 0;

export const AGING_BUCKETS = [
  { key: 'vigente', label: 'Vigente' },
  { key: 'd1_30', label: '1 a 30 días' },
  { key: 'd31_60', label: '31 a 60 días' },
  { key: 'd61_90', label: '61 a 90 días' },
  { key: 'd90_mas', label: 'Más de 90 días' }
];

export const agingBucket = (days) => (days <= 0 ? 'vigente' : days <= 30 ? 'd1_30' : days <= 60 ? 'd31_60' : days <= 90 ? 'd61_90' : 'd90_mas');

// Cartera por cliente y antigüedad (en centavos para sumar exacto). `customers` es la lista de clientes del negocio.
export const buildCartera = (sales, customers, today = localDateStr()) => {
  const byCustomer = new Map((customers || []).map((c) => [c.id, c]));
  const rows = new Map();
  const totals = { vigente: 0, d1_30: 0, d31_60: 0, d61_90: 0, d90_mas: 0, total: 0, count: 0 };
  (sales || []).filter(isOpenSale).forEach((sale) => {
    const cents = toCents(sale.total) - toCents(sale.paid_total);
    if (cents <= 0) return;
    const key = sale.customer_id || 'sin-cliente';
    if (!rows.has(key)) {
      const c = byCustomer.get(sale.customer_id);
      rows.set(key, { key, customer: c || null, name: c ? (c.legal_name || c.name || 'Sin nombre') : 'Sin cliente', tax_id: c?.tax_id || '', vigente: 0, d1_30: 0, d31_60: 0, d61_90: 0, d90_mas: 0, total: 0, count: 0, oldest: 0 });
    }
    const row = rows.get(key);
    const days = daysOverdue(sale, today);
    const bucket = agingBucket(days);
    row[bucket] += cents; row.total += cents; row.count += 1; row.oldest = Math.max(row.oldest, days);
    totals[bucket] += cents; totals.total += cents; totals.count += 1;
  });
  const list = [...rows.values()].sort((a, b) => b.total - a.total || a.name.localeCompare(b.name, 'es'));
  return { rows: list, totals };
};

// Resumen de un conjunto de ventas (las anuladas no cuentan).
export const summarize = (sales, today = localDateStr()) => {
  let sold = 0; let collected = 0; let pending = 0; let overdue = 0;
  (sales || []).forEach((s) => {
    if (s.status === 'anulada') return;
    sold += toCents(s.total); collected += toCents(s.paid_total);
    const bal = toCents(s.total) - toCents(s.paid_total);
    pending += bal;
    if (isOverdue(s, today)) overdue += bal;
  });
  return { sold: fromCents(sold), collected: fromCents(collected), pending: fromCents(pending), overdue: fromCents(overdue) };
};

// Comprobación previa de un abono en pantalla (la base vuelve a validar todo).
export const checkPayment = (sale, amount) => {
  const cents = Math.round(Number(amount) * 100);
  if (!Number.isFinite(Number(amount)) || amount === '' || amount === null) return 'Escribe el valor del abono.';
  if (Math.abs(Number(amount) * 100 - cents) > 1e-6) return 'El abono admite hasta 2 decimales.';
  if (cents <= 0) return 'El abono debe ser mayor que 0.';
  if (cents > toCents(sale.total) - toCents(sale.paid_total)) return `El abono supera el saldo (${money(balanceOf(sale))}).`;
  return null;
};

// ---- CSV para el contador ----
// Separador ";" y BOM (Excel en español lo abre en columnas y con tildes). Importes con coma decimal y sin separador de miles.
export const csvText = (value) => {
  let text = String(value ?? '');
  if (/^[=+\-@\t\r]/.test(text)) text = `'${text}`; // una celda que empiece así podría ejecutarse como fórmula
  return /[";\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text; // el separador es ";": la coma decimal no necesita comillas
};
export const csvAmount = (n) => (n === null || n === undefined || n === '' ? '' : (Math.round(Number(n) * 100) / 100).toFixed(2).replace('.', ','));
export const csvDate = (v) => {
  if (!v) return '';
  const raw = String(v);
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? '' : localDateStr(d);
};
export const toCsv = (headers, rows) => `﻿${[headers.join(';'), ...rows.map((r) => r.join(';'))].join('\r\n')}`;

export const salesToCsv = (sales, customers, quotesById = new Map()) => {
  const byId = new Map((customers || []).map((c) => [c.id, c]));
  return toCsv(
    ['Número', 'Fecha', 'Cliente', 'NIT o cédula', 'Estado', 'Subtotal', 'Descuentos', 'Impuestos', 'Total', 'Pagado', 'Saldo', 'Vence', 'Cotización', 'Motivo de anulación'],
    (sales || []).map((s) => {
      const c = byId.get(s.customer_id);
      const q = quotesById.get(s.quote_id);
      return [
        s.number, csvDate(s.created_at), c ? (c.legal_name || c.name) : '', c?.tax_id || '', SALE_STATUS_LABELS[s.status] || s.status,
        csvAmount(s.subtotal), csvAmount(s.discount_total), csvAmount(s.tax_total), csvAmount(s.total), csvAmount(s.paid_total), csvAmount(balanceOf(s)),
        csvDate(s.due_date), q ? `${q.number}${q.version > 1 ? ` v${q.version}` : ''}` : '', s.void_reason || ''
      ].map(csvText);
    })
  );
};

export const paymentsToCsv = (payments, salesById, customers) => {
  const byId = new Map((customers || []).map((c) => [c.id, c]));
  return toCsv(
    ['Fecha', 'Venta', 'Cliente', 'Valor', 'Medio', 'Referencia', 'Registró', 'Estado', 'Motivo de anulación'],
    (payments || []).map((p) => {
      const s = salesById.get(p.sale_id);
      const c = s ? byId.get(s.customer_id) : null;
      return [
        csvDate(p.paid_at), s?.number || '', c ? (c.legal_name || c.name) : '', csvAmount(p.amount), PAYMENT_METHOD_LABELS[p.method] || p.method,
        p.reference || '', p.recorded_by || '', p.voided_at ? 'Anulado' : 'Vigente', p.void_reason || ''
      ].map(csvText);
    })
  );
};

export const carteraToCsv = (cartera) =>
  toCsv(
    ['Cliente', 'NIT o cédula', 'Ventas abiertas', ...AGING_BUCKETS.map((b) => b.label), 'Saldo total'],
    [
      ...cartera.rows.map((r) => [r.name, r.tax_id, r.count, ...AGING_BUCKETS.map((b) => csvAmount(fromCents(r[b.key]))), csvAmount(fromCents(r.total))].map(csvText)),
      ['TOTAL', '', cartera.totals.count, ...AGING_BUCKETS.map((b) => csvAmount(fromCents(cartera.totals[b.key]))), csvAmount(fromCents(cartera.totals.total))].map(csvText)
    ]
  );

export const downloadCsv = (filename, content) => {
  const url = URL.createObjectURL(new Blob([content], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

// Líneas de la pantalla → arreglo JSON que espera public.create_sale (ventas a mano).
export const linesToSaleItems = (lines) => (lines || []).map((l) => ({
  product_id: l.product_id || null, sku: l.sku || null, description: String(l.description ?? '').trim(), unit: l.unit || null,
  quantity: Number(l.quantity), unit_price: Number(l.unit_price), discount_pct: Number(l.discount_pct || 0), tax_rate: Number(l.tax_rate || 0), tax_kind: l.tax_kind || 'iva'
}));
